import { useEffect, useRef, useState } from 'react';

import {
  DefaultError,
  useMutation,
  UseMutationResult,
  useQueryClient,
} from '@tanstack/react-query';

import { displayToastError } from '@/helpers';
import { invalidateIntegrationStatusRelatedQueries } from '@/modules/common';
import { mapEntityTypeToDTO } from '@/modules/common/mappers/entity-type.mapper';
import { mapIntegrationProviderToDTO } from '@/modules/common/mappers/integration.mapper';
import { EntityType } from '@/modules/common/types/entity-type';
import { OrganizationIntegrationStatus } from '@/modules/common/types/integration-status';
import { IntegrationServices } from '@/modules/common/types/integrations';

import { synchronizeIntegration } from '../api/integrations.api';

interface UseSynchronizeIntegrationParams {
  onSuccess?: () => void;
  onError?: () => void;
  currentStatus?: OrganizationIntegrationStatus;
}

export interface SynchronizeIntegrationParams {
  entities?: EntityType[];
  integrations?: IntegrationServices[];
}

/**
 * Hook to handle synchronization of integration data
 * Triggers refresh of data from the connected integration service
 * Tracks user-initiated refreshes and triggers success/error callbacks when sync completes
 */
export const useSynchronizeIntegration = ({
  onSuccess,
  onError,
  currentStatus,
}: UseSynchronizeIntegrationParams = {}): UseMutationResult<
  unknown,
  DefaultError,
  SynchronizeIntegrationParams | undefined
> => {
  const queryClient = useQueryClient();
  const [userInitiatedRefresh, setUserInitiatedRefresh] = useState(false);
  const previousStatusRef = useRef<OrganizationIntegrationStatus | undefined>(currentStatus);

  // Track status changes and trigger callbacks when user-initiated refresh completes
  useEffect(() => {
    if (
      userInitiatedRefresh &&
      currentStatus !== undefined &&
      previousStatusRef.current === OrganizationIntegrationStatus.Syncing
    ) {
      if (currentStatus === OrganizationIntegrationStatus.Idle) {
        onSuccess?.();
        setUserInitiatedRefresh(false);
      } else if (currentStatus === OrganizationIntegrationStatus.SyncFailed) {
        onError?.();
        setUserInitiatedRefresh(false);
      }
    }
    previousStatusRef.current = currentStatus;
  }, [currentStatus, userInitiatedRefresh, onSuccess, onError]);

  return useMutation({
    mutationFn: async (params?: SynchronizeIntegrationParams) => {
      setUserInitiatedRefresh(true);

      const integrationsDTO = params?.integrations
        ? params.integrations.map(integration => mapIntegrationProviderToDTO(integration))
        : undefined;

      const entitiesDTO = params?.entities
        ? params.entities.map(entity => mapEntityTypeToDTO(entity))
        : undefined;

      await synchronizeIntegration(
        params
          ? {
              integrations: integrationsDTO,
              entities: entitiesDTO,
            }
          : undefined
      );
    },
    onSuccess: async () => {
      // Invalidate integration status to refetch latest data
      await invalidateIntegrationStatusRelatedQueries(queryClient);
    },
    onError: (error: Error) => {
      displayToastError(error.message);
      setUserInitiatedRefresh(false);
    },
  });
};
